import React, { useState, useEffect, Suspense, lazy } from 'react';
import { adminApi } from '../../services/adminApi';

// Lazy load the RichTextEditor component to avoid build issues with react-quill
const RichTextEditor = lazy(() => import('./RichTextEditor'));

// Same default used server-side (my_app/models.py DEFAULT_ADVANCED_HERO_VIDEO_URL)
// when an 'advanced' variant doesn't set its own hero_video_url.
const DEFAULT_HERO_VIDEO_URL = '/videos/hero-background.mp4';

function AdminThemeLandingPages({ adminToken, tabName }) {
  // Form state
  const [formData, setFormData] = useState({
    theme_id: '',
    location_id: '',
    meetup_urlname: '',
    url_slug: '',
    variant_label: 'A',
    landing_page_type: 'basic',
    traffic_weight: 50,
    hero_video_url: '',
    hero_headline: 'It\'s time to meet some great people.',
    hero_image_url: '',
    benefit_1_title: 'Meet like minded people',
    benefit_1_text: 'Come along and meet like-minded people in a friendly environment.',
    benefit_2_title: '',
    benefit_2_text: '',
    benefit_3_title: '',
    benefit_3_text: '',
    description: '',
    cta_heading: 'Let\'s go!',
    cta_text: 'Continue',
    checkout_price_id: '',
    seo_title: '',
    seo_description: '',
    is_published: true,
  });

  // UI state
  const [landingPages, setLandingPages] = useState([]);
  const [themes, setThemes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [needsAutoPopulate, setNeedsAutoPopulate] = useState(false);
  // When adding a second (B) variant to an existing slug, the theme and
  // slug fields get locked to match the existing 'A' variant (or vice versa).
  const [lockedSlugGroup, setLockedSlugGroup] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState('');
  // Verifying the Meetup group on save: true while the check is in flight;
  // set to a group name (string) if the group was found but has zero
  // upcoming events right now, pending the admin's explicit confirmation.
  const [checkingMeetup, setCheckingMeetup] = useState(false);
  const [meetupZeroEventsWarning, setMeetupZeroEventsWarning] = useState(null);

  // AI review of the landing pages (copy/AEO/SEO/conversion, A/B and
  // cross-series comparisons) - see landing_page_ai.py
  const [latestReview, setLatestReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [showReview, setShowReview] = useState(false);

  // Group landing pages by url_slug so both A/B variants show together
  const groupedLandingPages = landingPages.reduce((groups, page) => {
    if (!groups[page.url_slug]) groups[page.url_slug] = [];
    groups[page.url_slug].push(page);
    return groups;
  }, {});

  // The image this variant will fall back to if its own Hero Image URL is left blank
  const selectedThemeImageUrl = themes.find(t => t.id === parseInt(formData.theme_id))?.image_url;

  // Load the most recent AI review, if one has ever been run
  useEffect(() => {
    if (tabName === 'ThemeLandingPages') {
      adminApi.getLatestLandingPageReview(adminToken)
        .then((review) => setLatestReview(review))
        .catch(() => setLatestReview(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabName]);

  const handleRunReview = async () => {
    setReviewing(true);
    setReviewError('');
    try {
      const review = await adminApi.createLandingPageReview(adminToken);
      setLatestReview(review);
      setShowReview(true);
    } catch (err) {
      setReviewError(err.message || 'Failed to generate review');
    } finally {
      setReviewing(false);
    }
  };

  // Load themes and landing pages
  useEffect(() => {
    if (tabName === 'ThemeLandingPages') {
      loadThemes();
      loadLandingPages();
      loadLocations();

      // Check for new theme message from AdminThemes
      const newThemeMessageStr = sessionStorage.getItem('newThemeMessage');
      if (newThemeMessageStr) {
        try {
          const messageData = JSON.parse(newThemeMessageStr);
          setSuccess(messageData.message);
          // Pre-select the theme in the form
          if (messageData.themeId) {
            setFormData(prev => ({ ...prev, theme_id: messageData.themeId.toString() }));
            setShowForm(true);
            setNeedsAutoPopulate(true); // Flag to trigger auto-populate when themes load
          }
          // Clear the message after reading it
          sessionStorage.removeItem('newThemeMessage');
        } catch (e) {
          console.error('Error parsing new theme message:', e);
        }
      }
    }
  }, [tabName]);

  // Auto-populate form fields when theme is selected (handles redirect from AdminThemes)
  useEffect(() => {
    // Only auto-populate if:
    // 1. needsAutoPopulate flag is set
    // 2. themes are loaded
    // 3. a theme_id is set
    if (needsAutoPopulate && themes.length > 0 && formData.theme_id) {
      const selectedTheme = themes.find(theme => theme.id === parseInt(formData.theme_id));
      if (selectedTheme) {
        const themeName = selectedTheme.theme_name;
        setFormData(prev => ({
          ...prev,
          hero_headline: themeName,
          seo_title: `${themeName} Gatherings`,
          seo_description: `Join ${themeName} gatherings and meet like-minded people.`,
          description: selectedTheme.default_description || prev.description,
        }));
        setNeedsAutoPopulate(false); // Clear flag after auto-populating
      }
    }
  }, [needsAutoPopulate, themes, formData.theme_id]);

  // The URL slug is never admin-entered - it's auto-generated server-side
  // from theme + Meetup group (not location - two groups can share a
  // city/venue) and frozen once a page exists (see
  // ThemeLandingPageRepository.build_url_slug), so every landing page's
  // slug is distinct per (theme, Meetup group) automatically and never
  // changes under an already-shared link. This effect only drives the
  // live preview shown while creating a fresh page (not adding a variant,
  // where the slug is copied exactly from the sibling instead, and not
  // editing, where it shows the real persisted value).
  useEffect(() => {
    if (editMode || lockedSlugGroup) return;
    const selectedTheme = themes.find(theme => theme.id === parseInt(formData.theme_id));
    const preview = (selectedTheme && formData.meetup_urlname?.trim())
      ? `${generateSlug(selectedTheme.theme_name)}-${generateSlug(formData.meetup_urlname)}`
      : '';
    setFormData(prev => (prev.url_slug === preview ? prev : { ...prev, url_slug: preview }));
  }, [formData.theme_id, formData.meetup_urlname, editMode, lockedSlugGroup, themes]);

  const loadThemes = async () => {
    try {
      const response = await fetch('/api/lookups/themes');
      if (response.ok) {
        const data = await response.json();
        setThemes(data);
      }
    } catch (err) {
      console.error('Failed to load themes:', err);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await adminApi.getLocations(adminToken);
      setLocations(data.filter((loc) => loc.is_active));
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadLandingPages = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getThemeLandingPages(adminToken);
      setLandingPages(data);
      setError('');
    } catch (err) {
      setError('Failed to load landing pages: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate URL-friendly slug from theme name
  const generateSlug = (themeName) => {
    if (!themeName) return '';
    return themeName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Special handling for theme selection
    if (name === 'theme_id' && value && !editMode) {
      const selectedTheme = themes.find(theme => theme.id === parseInt(value));
      if (selectedTheme) {
        const themeName = selectedTheme.theme_name;
        setFormData(prev => ({
          ...prev,
          theme_id: value,
          hero_headline: themeName,
          seo_title: `${themeName} Gatherings`,
          seo_description: `Join ${themeName} gatherings and meet like-minded people.`,
          // Only a starting point for a brand-new page - Add Variant/Clone
          // already carry the sibling's own description over instead, and
          // don't route through here (theme_id is locked/pre-filled there).
          description: selectedTheme.default_description || prev.description,
        }));
        return;
      }
    }

    let parsedValue = value;
    if (type === 'checkbox') {
      parsedValue = checked;
    } else if (name === 'traffic_weight') {
      parsedValue = value === '' ? '' : parseInt(value, 10);
    }

    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };


  // The actual create/update call, run only once the Meetup group has been
  // verified (or the admin explicitly chose to proceed despite a warning).
  const persistLandingPage = async (successNote = '') => {
    setLoading(true);
    try {
      if (editMode) {
        await adminApi.updateThemeLandingPage(editId, formData, adminToken);
        setSuccess(`Landing page updated successfully!${successNote}`);
      } else {
        await adminApi.createThemeLandingPage(formData, adminToken);
        setSuccess(`Landing page created successfully!${successNote}`);
      }

      resetForm();
      loadLandingPages();
    } catch (err) {
      setError('Failed to save landing page: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    const missingFields = [];
    if (!formData.theme_id) missingFields.push('Theme');
    if (!formData.location_id) missingFields.push('City');
    if (!formData.meetup_urlname?.trim()) missingFields.push('Meetup Group URL Name');
    if (!formData.hero_headline?.trim()) missingFields.push('Hero Headline');
    if (!formData.benefit_1_title?.trim()) missingFields.push('Benefit 1 Title');
    if (!formData.benefit_1_text?.trim()) missingFields.push('Benefit 1 Text');
    if (!formData.description?.trim()) missingFields.push('Description');
    if (!formData.cta_heading?.trim()) missingFields.push('CTA Heading');
    if (!formData.cta_text?.trim()) missingFields.push('CTA Button Text');
    if (!formData.seo_title?.trim()) missingFields.push('SEO Title');
    if (!formData.seo_description?.trim()) missingFields.push('SEO Description');
    if (formData.traffic_weight === '' || formData.traffic_weight === null || isNaN(formData.traffic_weight)) {
      missingFields.push('Traffic Weight');
    }

    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }

    setError('');
    setSuccess('');
    setMeetupZeroEventsWarning(null);
    setCheckingMeetup(true);

    const urlname = formData.meetup_urlname.trim();
    try {
      const result = await adminApi.verifyMeetupGroup(urlname, adminToken);
      setCheckingMeetup(false);

      if (!result.found) {
        setError(
          `No Meetup group found for "${urlname}". This must be just the slug from the group's own URL - ` +
          `meetup.com/${urlname ? '<this-part>' : '...'}/ - not the full link and not the group's display name. ` +
          `Double-check it against the group's page on meetup.com.`
        );
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        return;
      }

      if (result.event_count === 0) {
        // Found, but nothing upcoming right now - could be correct (a real
        // group between events) or a typo pointing at the wrong group, so
        // ask rather than silently saving or silently blocking.
        setMeetupZeroEventsWarning(result.group_name || urlname);
        return;
      }

      await persistLandingPage();
    } catch (err) {
      setCheckingMeetup(false);
      // Couldn't even complete the check (Meetup outage, or the Meetup
      // integration isn't connected in this environment) - not something
      // the admin did wrong, so don't block them on it. Events will show
      // up once the next sync succeeds.
      await persistLandingPage(` (Note: couldn't verify with Meetup just now - ${err.message})`);
    }
  };

  const handleSaveAnyway = async () => {
    setMeetupZeroEventsWarning(null);
    await persistLandingPage(' (Meetup group has no upcoming events right now.)');
  };

  const handleCancelMeetupWarning = () => {
    setMeetupZeroEventsWarning(null);
  };

  const handleEdit = (page) => {
    setFormData({
      theme_id: page.theme_id,
      location_id: page.location_id || '',
      meetup_urlname: page.meetup_urlname || '',
      url_slug: page.url_slug,
      variant_label: page.variant_label || 'A',
      landing_page_type: page.landing_page_type || 'basic',
      traffic_weight: page.traffic_weight ?? 50,
      // Edit the raw per-variant override, not the effective (possibly
      // inherited) value - otherwise saving with no changes would pin the
      // inherited theme image / default video onto this variant forever.
      hero_video_url: page.hero_video_override || '',
      hero_headline: page.hero_headline || 'It\'s time to meet some great people.',
      hero_image_url: page.hero_image_override || '',
      benefit_1_title: page.benefit_1_title || 'Meet like minded people',
      benefit_1_text: page.benefit_1_text || 'Come along and meet like-minded people in a friendly environment.',
      benefit_2_title: page.benefit_2_title || '',
      benefit_2_text: page.benefit_2_text || '',
      benefit_3_title: page.benefit_3_title || '',
      benefit_3_text: page.benefit_3_text || '',
      description: page.description || '',
      cta_heading: page.cta_heading || 'Let\'s go!',
      cta_text: page.cta_text || 'Continue',
      checkout_price_id: page.checkout_price_id || '',
      seo_title: page.seo_title || '',
      seo_description: page.seo_description || '',
      is_published: page.is_published,
    });
    setEditMode(true);
    setEditId(page.id);
    setLockedSlugGroup(null);
    setShowForm(true);
    setError('');
    setSuccess('');
    // Delay scroll to ensure form is rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 100);
  };

  // Add the missing A or B variant to an existing landing page group. Theme
  // and URL slug are locked to match the sibling variant already in the group.
  const handleAddVariant = (siblingPage) => {
    const newVariantLabel = siblingPage.variant_label === 'A' ? 'B' : 'A';
    setFormData({
      theme_id: siblingPage.theme_id,
      // Same real-world audience/location as its sibling variant, so the
      // Meetup group and city carry over by default (still editable).
      location_id: siblingPage.location_id || '',
      meetup_urlname: siblingPage.meetup_urlname || '',
      url_slug: siblingPage.url_slug,
      variant_label: newVariantLabel,
      landing_page_type: 'basic',
      traffic_weight: 50,
      hero_video_url: '',
      hero_headline: siblingPage.hero_headline || 'It\'s time to meet some great people.',
      hero_image_url: '',
      benefit_1_title: 'Meet like minded people',
      benefit_1_text: 'Come along and meet like-minded people in a friendly environment.',
      benefit_2_title: '',
      benefit_2_text: '',
      benefit_3_title: '',
      benefit_3_text: '',
      description: siblingPage.description || '',
      cta_heading: siblingPage.cta_heading || 'Let\'s go!',
      cta_text: siblingPage.cta_text || 'Continue',
      checkout_price_id: siblingPage.checkout_price_id || '',
      seo_title: siblingPage.seo_title || '',
      seo_description: siblingPage.seo_description || '',
      is_published: true,
    });
    setEditMode(false);
    setEditId(null);
    setLockedSlugGroup({ theme_id: siblingPage.theme_id, url_slug: siblingPage.url_slug });
    setShowForm(true);
    setError('');
    setSuccess('');
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 100);
  };

  // Clone an existing landing page into a fresh, independent one. Unlike
  // handleAddVariant (which locks Theme/Meetup group/slug to match a
  // sibling), a clone needs its own Theme + Meetup group - the slug is
  // server-derived from that pair and must be unique per (slug, variant),
  // so those fields are left blank for the admin to fill in rather than
  // copied, while every content field is copied over as a starting point.
  const handleClone = (page) => {
    setFormData({
      theme_id: '',
      location_id: '',
      meetup_urlname: '',
      url_slug: '',
      variant_label: 'A',
      landing_page_type: page.landing_page_type || 'basic',
      traffic_weight: page.traffic_weight ?? 50,
      hero_video_url: page.hero_video_override || '',
      hero_headline: page.hero_headline || 'It\'s time to meet some great people.',
      hero_image_url: page.hero_image_override || '',
      benefit_1_title: page.benefit_1_title || 'Meet like minded people',
      benefit_1_text: page.benefit_1_text || 'Come along and meet like-minded people in a friendly environment.',
      benefit_2_title: page.benefit_2_title || '',
      benefit_2_text: page.benefit_2_text || '',
      benefit_3_title: page.benefit_3_title || '',
      benefit_3_text: page.benefit_3_text || '',
      description: page.description || '',
      cta_heading: page.cta_heading || 'Let\'s go!',
      cta_text: page.cta_text || 'Continue',
      checkout_price_id: page.checkout_price_id || '',
      seo_title: page.seo_title || '',
      seo_description: page.seo_description || '',
      is_published: page.is_published,
    });
    setEditMode(false);
    setEditId(null);
    setLockedSlugGroup(null);
    setShowForm(true);
    setError('');
    setSuccess('Cloned - pick a Theme and Meetup Group for the new landing page, then Create.');
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this landing page?')) {
      return;
    }

    try {
      setLoading(true);
      await adminApi.deleteThemeLandingPage(pageId, adminToken);
      setSuccess('Landing page deleted successfully!');
      loadLandingPages();
    } catch (err) {
      setError('Failed to delete landing page: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      theme_id: '',
      location_id: '',
      meetup_urlname: '',
      url_slug: '',
      variant_label: 'A',
      landing_page_type: 'basic',
      traffic_weight: 50,
      hero_video_url: '',
      hero_headline: 'It\'s time to meet some great people.',
      hero_image_url: '',
      benefit_1_title: 'Meet like minded people',
      benefit_1_text: 'Come along and meet like-minded people in a friendly environment.',
      benefit_2_title: '',
      benefit_2_text: '',
      benefit_3_title: '',
      benefit_3_text: '',
      description: '',
      cta_heading: 'Let\'s go!',
      cta_text: 'Continue',
      checkout_price_id: '',
      seo_title: '',
      seo_description: '',
      is_published: true,
    });
    setEditMode(false);
    setEditId(null);
    setLockedSlugGroup(null);
    setShowForm(false);
    setError('');
    setSuccess('');
    setCheckingMeetup(false);
    setMeetupZeroEventsWarning(null);
  };

  const openPreview = (slug, variantLabel) => {
    const qs = variantLabel ? `?preview_variant=${variantLabel}` : '';
    window.open(`/theme/${slug}${qs}`, '_blank');
  };

  const getFullUrl = (slug) => `${window.location.origin}/theme/${slug}`;

  const handleCopyUrl = async (slug) => {
    const url = getFullUrl(slug);
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(prev => (prev === slug ? '' : prev)), 2000);
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Theme Landing Pages</h2>

      {/* AI Review: Claude reviews every landing page's real stored content
          (event description, hero copy, benefits, SEO fields) plus 30-day
          Mixpanel funnel/A-B data for AEO/SEO quality, likely conversion/
          retention impact, and differences within A/B tests and across
          event series. See my_app/landing_page_ai.py. */}
      <div style={{ marginBottom: '25px', padding: '20px', border: '1px solid #d6c8f0', borderRadius: '8px', backgroundColor: '#faf8ff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h4 style={{ margin: '0 0 4px 0' }}>AI Review</h4>
            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
              {latestReview
                ? `Last reviewed: ${new Date(latestReview.created_at).toLocaleString()}`
                : 'Not reviewed yet.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleRunReview}
            disabled={reviewing}
            style={{
              padding: '10px 20px',
              backgroundColor: reviewing ? '#ccc' : '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: reviewing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {reviewing ? 'Reviewing... (can take a minute or two)' : 'Review Landing Pages'}
          </button>
        </div>

        {reviewError && (
          <div style={{
            marginTop: '15px', color: 'red', padding: '10px',
            border: '1px solid red', borderRadius: '4px', backgroundColor: '#ffe6e6', fontSize: '13px'
          }}>
            {reviewError}
          </div>
        )}

        {latestReview && (
          <div style={{ marginTop: '15px' }}>
            <button
              type="button"
              onClick={() => setShowReview((v) => !v)}
              style={{
                padding: '5px 15px', backgroundColor: '#6c757d', color: 'white',
                border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px'
              }}
            >
              {showReview ? 'Hide review' : 'Show review'}
            </button>

            {showReview && (
              <div style={{ marginTop: '15px' }}>
                <p style={{ fontSize: '14px', color: '#333' }}>
                  {latestReview.review_data.overall_summary}
                </p>

                {latestReview.review_data.cross_series_observations?.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Cross-series observations</p>
                    <ul style={{ fontSize: '13px', color: '#333', margin: 0, paddingLeft: '20px' }}>
                      {latestReview.review_data.cross_series_observations.map((obs, i) => <li key={i}>{obs}</li>)}
                    </ul>
                  </div>
                )}

                {latestReview.review_data.ab_test_ideas?.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Suggested A/B tests</p>
                    <ul style={{ fontSize: '13px', color: '#333', margin: 0, paddingLeft: '20px' }}>
                      {latestReview.review_data.ab_test_ideas.map((idea, i) => <li key={i}>{idea}</li>)}
                    </ul>
                  </div>
                )}

                {latestReview.review_data.pages?.map((page, i) => (
                  <div key={i} style={{ borderTop: '1px solid #eee', padding: '10px 0' }}>
                    <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                      {page.url_slug} (variant {page.variant_label}){page.theme_name ? ` — ${page.theme_name}` : ''}
                    </p>
                    {page.issues?.length > 0 && (
                      <ul style={{ fontSize: '13px', color: '#a94442', margin: '0 0 5px 0', paddingLeft: '20px' }}>
                        {page.issues.map((issue, j) => <li key={j}>{issue}</li>)}
                      </ul>
                    )}
                    {page.suggestions?.length > 0 && (
                      <ul style={{ fontSize: '13px', color: '#333', margin: 0, paddingLeft: '20px' }}>
                        {page.suggestions.map((s, j) => <li key={j}>{s}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: '#d4edda',
          color: '#155724',
          borderRadius: '4px'
        }}>
          {success}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => (showForm ? resetForm() : (resetForm(), setShowForm(true)))}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showForm ? 'Hide Form' : 'Create New Landing Page'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: '40px' }}>
          <h3>{editMode ? 'Edit Landing Page' : lockedSlugGroup ? `Add Variant ${formData.variant_label} to /theme/${formData.url_slug}` : 'Create Landing Page'}</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Theme *
            </label>
            {!editMode && !lockedSlugGroup && (
              <div style={{
                padding: '8px 12px',
                marginBottom: '8px',
                backgroundColor: '#e7f3ff',
                borderLeft: '4px solid #007bff',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#004085'
              }}>
                💡 A theme can have multiple landing pages - one per Meetup group (not location, since two
                groups can share a city) - each with its own auto-generated URL slug and its own A/B pair.
                Use "Add Variant" in the table below to add a B variant to an existing page.
              </div>
            )}
            {(editMode || lockedSlugGroup) && (
              <div style={{
                padding: '8px 12px',
                marginBottom: '8px',
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#856404'
              }}>
                {lockedSlugGroup
                  ? `Theme and URL slug are locked to match the existing variant for /theme/${lockedSlugGroup.url_slug}.`
                  : 'Theme cannot be changed when editing an existing landing page.'}
              </div>
            )}
            <select
              name="theme_id"
              value={formData.theme_id}
              onChange={handleInputChange}
              required
              disabled={editMode || !!lockedSlugGroup}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: (editMode || lockedSlugGroup) ? '#f5f5f5' : 'white',
                cursor: (editMode || lockedSlugGroup) ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">Select a theme...</option>
              {themes.map(theme => (
                <option key={theme.id} value={theme.id}>
                  {theme.theme_name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              URL Slug
            </label>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>
              Auto-generated from the Theme and Meetup Group above, and permanent once this page is
              created - not editable, so every page's URL is always distinct per Meetup group (even
              two groups in the same city) and never changes under a link you've already shared or
              pasted into Meetup.
            </p>
            <input
              type="text"
              name="url_slug"
              value={formData.url_slug || (editMode || lockedSlugGroup ? '' : 'Select a theme and enter a Meetup group…')}
              readOnly
              disabled
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: '#f5f5f5',
                color: formData.url_slug ? '#333' : '#999',
                fontFamily: 'monospace',
              }}
            />
          </div>

          <div style={{ marginBottom: '15px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '160px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                City *
              </label>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>
                Shown prominently to visitors on the public Event Series picker so they can tell
                this option apart from others for the same theme - required so no one picks the
                wrong city by mistake.
              </p>
              <select
                name="location_id"
                value={formData.location_id}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
              >
                <option value="">Select a city…</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.city}{loc.area ? ` — ${loc.area}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Meetup Group URL Name *
              </label>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>
                The slug from this group's Meetup URL (meetup.com/&lt;this-value&gt;/).
                That group's upcoming events are pulled in as soon as you save (then kept in sync
                hourly) and shown on this page - required so visitors who land here without coming
                from Meetup (e.g. via search) still see the real date/time/location.
                {lockedSlugGroup
                  ? ' Locked to match the other A/B variant: a visitor on this shared URL can land on either one, so both must point at the same group.'
                  : ' Both A/B variants of a slug must use the same group.'}
              </p>
              <input
                type="text"
                name="meetup_urlname"
                value={formData.meetup_urlname}
                onChange={handleInputChange}
                required
                disabled={!!lockedSlugGroup}
                placeholder="e.g. Birmingham-Tech-Network"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: lockedSlugGroup ? '#f5f5f5' : 'white',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '140px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Variant *
              </label>
              <select
                name="variant_label"
                value={formData.variant_label}
                onChange={handleInputChange}
                disabled={!!lockedSlugGroup}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: lockedSlugGroup ? '#f5f5f5' : 'white',
                }}
              >
                <option value="A">A</option>
                <option value="B">B</option>
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '160px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Format *
              </label>
              <select
                name="landing_page_type"
                value={formData.landing_page_type}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
              >
                <option value="basic">Basic (standard landing page)</option>
                <option value="advanced">Advanced (homepage-style, video hero)</option>
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '160px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Traffic Weight * <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666' }}>(probability of being shown)</span>
              </label>
              <input
                type="number"
                name="traffic_weight"
                value={formData.traffic_weight}
                onChange={handleInputChange}
                min="0"
                max="100"
                required
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
              />
            </div>
          </div>

          {formData.landing_page_type === 'advanced' && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Hero Video URL
              </label>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>
                Defaults to the same video used on the homepage ({DEFAULT_HERO_VIDEO_URL}) if left blank.
                Only set this if you want this specific variant to use a different video.
              </p>
              <input
                type="text"
                name="hero_video_url"
                value={formData.hero_video_url}
                onChange={handleInputChange}
                placeholder={`Leave blank to use ${DEFAULT_HERO_VIDEO_URL}`}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Hero Headline *
            </label>
            <input
              type="text"
              name="hero_headline"
              value={formData.hero_headline}
              onChange={handleInputChange}
              required
              placeholder="Connect with Finance Professionals"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Hero Image URL
            </label>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>
              {selectedThemeImageUrl
                ? <>Defaults to the selected theme's image ({selectedThemeImageUrl}) if left blank. Only set this if you want this specific variant to use a different image.</>
                : "Defaults to the selected theme's image (set in the Themes admin panel) if left blank. Only set this if you want this specific variant to use a different image."}
            </p>
            <input
              type="text"
              name="hero_image_url"
              value={formData.hero_image_url}
              onChange={handleInputChange}
              placeholder="Leave blank to use the theme's image"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <h4>Benefits</h4>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Benefit 1 Title *
            </label>
            <input
              type="text"
              name="benefit_1_title"
              value={formData.benefit_1_title}
              onChange={handleInputChange}
              required
              placeholder="Network with Experts"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Benefit 1 Text *
            </label>
            <textarea
              name="benefit_1_text"
              value={formData.benefit_1_text}
              onChange={handleInputChange}
              required
              rows="3"
              placeholder="Connect with like-minded professionals..."
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Benefit 2 Title
            </label>
            <input
              type="text"
              name="benefit_2_title"
              value={formData.benefit_2_title}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Benefit 2 Text
            </label>
            <textarea
              name="benefit_2_text"
              value={formData.benefit_2_text}
              onChange={handleInputChange}
              rows="3"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Benefit 3 Title
            </label>
            <input
              type="text"
              name="benefit_3_title"
              value={formData.benefit_3_title}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Benefit 3 Text
            </label>
            <textarea
              name="benefit_3_text"
              value={formData.benefit_3_text}
              onChange={handleInputChange}
              rows="3"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <h4>Event Description</h4>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Description (Rich Text) *
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Paste your event description here. You can use formatting like bold, bullet points, etc.
            </p>
            <Suspense fallback={
              <div style={{
                padding: '20px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                textAlign: 'center',
                color: '#666'
              }}>
                Loading editor...
              </div>
            }>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
              />
            </Suspense>
          </div>

          <h4>Checkout Pricing</h4>
          <p style={{ fontSize: '13px', color: '#666', marginTop: '-8px', marginBottom: '12px' }}>
            Optional. Charge a different amount for visitors who arrive via this landing page
            (e.g. a different city or event type). Leave blank to use the site's default price.
            The actual amount charged is whatever this Price ID is configured to charge in
            Memberstack - it isn't shown here, so double-check the amount in the Memberstack
            Dashboard directly rather than assuming from the ID.
          </p>

          <div style={{
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '15px',
            fontSize: '13px',
            color: '#444',
            lineHeight: '1.6',
          }}>
            <strong>How to set a custom price:</strong> checkout runs through Memberstack
            (not Stripe directly), so a new price is created in Memberstack, not the
            Stripe Dashboard - Memberstack creates the matching Stripe Price behind the
            scenes once it's connected to a plan.
            <ol style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
              <li>
                Log into the{' '}
                <a href="https://app.memberstack.io/" target="_blank" rel="noopener noreferrer">
                  Memberstack Dashboard
                </a>{' '}
                for this environment (dev vs. prod uses a different Memberstack account -
                use whichever matches the site you're editing).
              </li>
              <li>Go to <strong>Plans &amp; Pricing</strong> and open the existing OkGather membership plan (the one behind the current default price).</li>
              <li>Add a new <strong>Price</strong> to that plan at the amount you want to charge. Don't create a whole new plan - it needs to be a price on the same plan so it grants the same membership access.</li>
              <li>Copy the new price's ID - it looks like <code>prc_xxxxxxxxxxxxxxxx</code>. A raw Stripe price ID (<code>price_...</code>) won't work here.</li>
              <li>Paste that ID into <strong>Memberstack Price ID</strong> below - that's all that's needed for this landing page to charge that price.</li>
            </ol>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Memberstack Price ID
            </label>
            <input
              type="text"
              name="checkout_price_id"
              value={formData.checkout_price_id}
              onChange={handleInputChange}
              placeholder="prc_okgather-membership-xxxxxxxx"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <h4>SEO & Settings</h4>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              CTA Heading *
            </label>
            <input
              type="text"
              name="cta_heading"
              value={formData.cta_heading}
              onChange={handleInputChange}
              required
              placeholder="Let's go!"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              CTA Button Text * <span style={{ color: '#dc3545', fontSize: '13px', fontWeight: 'normal' }}>(CAUTION: changing this may have negative effect)</span>
            </label>
            <input
              type="text"
              name="cta_text"
              value={formData.cta_text}
              onChange={handleInputChange}
              required
              placeholder="Continue"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              SEO Title *
            </label>
            <input
              type="text"
              name="seo_title"
              value={formData.seo_title}
              onChange={handleInputChange}
              required
              placeholder="Finance Networking | OkGather"
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              SEO Description *
            </label>
            <textarea
              name="seo_description"
              value={formData.seo_description}
              onChange={handleInputChange}
              required
              rows="2"
              placeholder="Connect with finance professionals in your area..."
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_published"
                checked={formData.is_published}
                onChange={handleInputChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontWeight: 'bold' }}>Published</span>
            </label>
          </div>

          {checkingMeetup && (
            <div style={{
              padding: '10px 12px',
              marginBottom: '15px',
              backgroundColor: '#e7f3ff',
              borderLeft: '4px solid #007bff',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#004085'
            }}>
              🔄 Checking Meetup group "{formData.meetup_urlname}"…
            </div>
          )}

          {meetupZeroEventsWarning && (
            <div style={{
              padding: '10px 12px',
              marginBottom: '15px',
              backgroundColor: '#fff3cd',
              borderLeft: '4px solid #ffc107',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#856404'
            }}>
              ⚠️ "{meetupZeroEventsWarning}" was found on Meetup, but has no upcoming events right now.
              That's normal for a real group between events, but also what a wrong/mistyped group looks
              like - worth double-checking on meetup.com if you're not sure.
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={handleSaveAnyway}
                  disabled={loading}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#ffc107',
                    color: '#000',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    fontSize: '13px',
                  }}
                >
                  {loading ? 'Saving...' : 'Save Anyway'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelMeetupWarning}
                  disabled={loading}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#fff',
                    color: '#856404',
                    border: '1.5px solid #856404',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              disabled={loading || checkingMeetup || !!meetupZeroEventsWarning}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (loading || checkingMeetup || meetupZeroEventsWarning) ? 'not-allowed' : 'pointer'
              }}
            >
              {checkingMeetup ? 'Checking Meetup group…' : loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
            </button>

            {editMode && (
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      <h3>Existing Landing Pages</h3>
      {loading && <p>Loading...</p>}

      {landingPages.length === 0 && !loading && (
        <p>No landing pages created yet.</p>
      )}

      {landingPages.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: '20px' }}>
        <table style={{
          width: '100%',
          minWidth: '900px',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Meetup Group URL Name</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Theme</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Slug</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Location</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Variant</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Format</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Weight</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Price ID</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Status</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(groupedLandingPages)
              .sort((slugA, slugB) => {
                const nameA = groupedLandingPages[slugA][0].theme_name || '';
                const nameB = groupedLandingPages[slugB][0].theme_name || '';
                return nameA.localeCompare(nameB);
              })
              .map(slug => {
                const variants = groupedLandingPages[slug].sort((a, b) => a.variant_label.localeCompare(b.variant_label));
                const canAddVariant = variants.length === 1;
                return variants.map((page, idx) => (
                  <tr key={page.id} style={{ borderBottom: idx === variants.length - 1 ? '2px solid #dee2e6' : '1px solid #eee' }}>
                    {/* First/far-left column, and visually the most prominent -
                        this is the value that actually drives what a visitor
                        sees, so it needs to be impossible to miss. Not
                        rowSpan'd like Theme/Slug below: unlike those, a
                        variant's own Meetup Group isn't guaranteed to match
                        its sibling (only defaulted to match when a variant is
                        first added, via "Add Variant"), so each row shows its
                        own actual value rather than hiding a possible
                        mismatch between A and B. */}
                    <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '15px', fontWeight: 'bold' }}>
                      {page.meetup_urlname || '—'}
                    </td>
                    {idx === 0 && (
                      <>
                        <td style={{ padding: '10px' }} rowSpan={variants.length}>{page.theme_name}</td>
                        <td style={{ padding: '10px' }} rowSpan={variants.length}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                              {getFullUrl(page.url_slug)}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopyUrl(page.url_slug)}
                              title="Copy URL"
                              style={{
                                padding: '3px 8px',
                                backgroundColor: copiedSlug === page.url_slug ? '#28a745' : '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                flexShrink: 0,
                              }}
                            >
                              {copiedSlug === page.url_slug ? '✓ Copied!' : '📋 Copy'}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                    {/* Not rowSpan'd like Theme/Slug above - see the Meetup
                        Group column's own comment for why. */}
                    <td style={{ padding: '10px' }}>{page.location_name || '—'}</td>
                    <td style={{ padding: '10px', fontWeight: 'bold' }}>{page.variant_label}</td>
                    <td style={{ padding: '10px', textTransform: 'capitalize' }}>{page.landing_page_type || 'basic'}</td>
                    <td style={{ padding: '10px' }}>{page.traffic_weight ?? 50}</td>
                    <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '12px' }}>
                      {page.checkout_price_id
                        ? page.checkout_price_id
                        : <span style={{ color: '#999', fontFamily: 'inherit' }}>default</span>}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: page.is_published ? '#d4edda' : '#f8d7da',
                        color: page.is_published ? '#155724' : '#721c24'
                      }}>
                        {page.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEdit(page)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openPreview(page.url_slug, page.variant_label)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => handleClone(page)}
                          title="Copy this page's content into a new landing page under a different Theme/Meetup Group"
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#6f42c1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Clone
                        </button>
                        {idx === 0 && canAddVariant && (
                          <button
                            onClick={() => handleAddVariant(page)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            + Add Variant {page.variant_label === 'A' ? 'B' : 'A'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(page.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ));
              })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export default AdminThemeLandingPages;
